import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { getIcon } from '@/common/lib/getIcon';
import { Playdeck } from '@/common/lib/playdeck';
import { type BingoRewardType } from '@/common/types/bingo';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import { H2, StringXSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { Picture } from '@/components/ui/Picture/Picture';
import { claimLineRewards } from '@/redux/reducers/bingoReducer';
import { addToBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import lightPng from '/img/patterns/light.png';
import lightWebp from '/img/patterns/light.webp';

const STATIC_IMG: Record<string, string> = {
  goldTicket: '/img/icons/ticket-gold.webp',
  silverTicket: '/img/icons/ticket-silver.webp',
  diamondTicket: '/img/icons/ticket-diamond.webp',
  starPoints: '/img/icons/star-large.png',
  boardStarPoints: '/img/icons/star-large.png',
  bingoBalls: '/img/bingo/bingo-icon.png',
};

const CURRENCY_MAP: Record<string, 'tgem' | 'dice' | 'star' | 'cash'> = {
  tgem: 'tgem',
  dice: 'dice',
  cash: 'cash',
};

const REWARD_LABELS: Record<string, string> = {
  tgem: 'T-Gems',
  dice: 'Dice',
  starPoints: 'Star Points',
  boardStarPoints: 'Star Points',
  goldTicket: 'Gold Ticket',
  silverTicket: 'Silver Ticket',
  diamondTicket: 'Diamond Ticket',
  bingoBalls: 'Bingo Balls',
};

function getRewardIcon(type: string): string | null {
  if (STATIC_IMG[type]) return STATIC_IMG[type];
  const icon = getIcon(type);

  if (icon) return icon.webp ?? icon.png;

  return null;
}

interface BingoRewardItemProps {
  rewardType: BingoRewardType;
  value: number;
}

const BingoRewardItem: FC<BingoRewardItemProps> = ({ rewardType, value }) => {
  const currencyKind = CURRENCY_MAP[rewardType];
  const imgSrc = !currencyKind ? getRewardIcon(rewardType) : null;

  return (
    <div className="flex flex-col items-center gap-1">
      {currencyKind ? (
        <CurrencyImage kind={currencyKind} size="64px" />
      ) : imgSrc ? (
        <img
          src={imgSrc}
          alt={rewardType}
          className="w-16 h-16 object-contain"
        />
      ) : null}
      <H2 weight="extrabold" className="text-white leading-none">
        +{value}
      </H2>
      <StringXSmall className="text-white/50">
        {REWARD_LABELS[rewardType] ?? rewardType.replace(/_/g, ' ')}
      </StringXSmall>
    </div>
  );
};

const BingoLineRewardsModal: FC = () => {
  const dispatch = useAppDispatch();
  const [isClaiming, setIsClaiming] = useState(false);
  const isRolling = useAppSelector((state) => state.bingo.isRolling);
  const pendingLineRewards = useAppSelector(
    (state) => state.bingo.pendingLineRewards,
  );

  const isVisible = !isRolling && pendingLineRewards.length > 0;

  const grouped = pendingLineRewards.reduce<
    { rewardType: BingoRewardType; value: number }[]
  >((acc, r) => {
    const existing = acc.find((x) => x.rewardType === r.rewardType);

    if (existing) {
      existing.value += r.value;
    } else {
      acc.push({ rewardType: r.rewardType, value: r.value });
    }

    return acc;
  }, []);

  const handleClaim = (): void => {
    setIsClaiming(true);
  };

  const handleConfettiComplete = (): void => {
    const balanceUpdate: Record<string, number> = {};
    const rewardToBalance: Record<string, string> = {
      cash: 'cash',
      tgem: 'tgem',
      dice: 'dice',
      starPoints: 'point',
      boardStarPoints: 'point',
      bingoBalls: 'bingoBalls',
    };

    for (const r of grouped) {
      const key = rewardToBalance[r.rewardType];

      if (key) {
        balanceUpdate[key] = (balanceUpdate[key] || 0) + r.value;
      }
    }

    if (Object.keys(balanceUpdate).length > 0) {
      dispatch(addToBalance(balanceUpdate));
    }

    const reward: Record<string, number> = {};

    for (const r of grouped) {
      reward[r.rewardType] = (reward[r.rewardType] || 0) + r.value;
    }

    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'bingo_rewards_claimed',
      user_properties: {},
      event_properties: { reward },
    });

    setIsClaiming(false);
    dispatch(claimLineRewards());
  };
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 flex flex-col items-center justify-center z-[9999] px-6"
          style={{
            background:
              'linear-gradient(180deg, rgba(24,26,32,0) 0%, rgba(24,26,32,0.85) 25%, #181a20 55%, #181a20 100%)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {isClaiming && (
            <Confetti
              className="inset-0 absolute"
              loop={false}
              onComplete={handleConfettiComplete}
              speed={1.5}
            />
          )}

          <Picture
            img={lightPng}
            webp={lightWebp}
            alt=""
            className="absolute w-full h-max [&>img]:h-auto mix-blend-overlay -z-10 pointer-events-none"
          />

          <div className="flex items-end justify-center gap-6 flex-wrap mb-8">
            {grouped.map((r, idx) => (
              <motion.div
                key={r.rewardType}
                initial={{ opacity: 0, y: 20, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  delay: idx * 0.1,
                  duration: 0.35,
                  ease: [0.34, 1.56, 0.64, 1],
                }}
              >
                <BingoRewardItem rewardType={r.rewardType} value={r.value} />
              </motion.div>
            ))}
          </div>

          <motion.div
            className="w-full"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: grouped.length * 0.1 + 0.1, duration: 0.3 }}
          >
            <MainButton
              text={t('Claim reward')}
              onClick={handleClaim}
              disabled={isClaiming}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { BingoLineRewardsModal };
